import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Check, ImagePlus, Loader2, Plus, ScanFace, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { STORYBOOK_PHOTO_ACCEPT, StorybookPhotoError, prepareStorybookPhoto } from '@/lib/storybook-photo';
import { detectStorybookFaces } from '@/lib/storybook-faces';
import {
  NOT_IN_BOOK,
  assignBlock,
  assignFace,
  boxCentre,
  boxSidePx,
  faceTooSmall,
  photoBytes,
  photoCoverage,
  photoIsUsed,
  removeFace,
  squareFaceBox,
  updateFaceBox,
  type StorybookAssignBlock,
  type StorybookPhotoItem,
  type StorybookTagLimits,
  type StorybookTaggablePerson,
  type StorybookTaggedFace,
} from '@/lib/storybook-tagging';

export interface StorybookPhotoTaggerProps {
  /** Prefix for element ids (the builder and the book page's dialog each have one tagger). */
  idPrefix: string;
  /** The whole cast in order; a friend (photoAllowed false) appears only in the summary. */
  people: StorybookTaggablePerson[];
  /**
   * Keys of people who already have a photo on the server that stays unless
   * they are tagged again (the book page's "Update photos").
   */
  keptPhotos?: ReadonlySet<string>;
  photos: StorybookPhotoItem[];
  onPhotosChange: Dispatch<SetStateAction<StorybookPhotoItem[]>>;
  limits: StorybookTagLimits;
  onBusyChange?: (busy: boolean) => void;
  /** An error for the whole step (the server's answer). */
  error?: string;
  /** Errors on single photos (a server error on photos[i] / faces[i]), by photo key. */
  photoErrors?: Record<string, string>;
  /** The parent changed something in this photo (its server error no longer applies). */
  onPhotoEdited?: (photoKey: string) => void;
}

const ERROR_KEYS = {
  heic: 'heic',
  unsupported: 'unsupported',
  unreadable: 'unreadable',
  too_large: 'tooLarge',
  too_small: 'tooSmall',
} as const;

/** Below this many bytes left in the request's total, a new photo would come out too blurry to draw from. */
const MIN_PHOTO_BUDGET = 128 * 1024;
/** Hand-placed boxes start at this share of the photo's short side. */
const MANUAL_START_SHARE = 0.25;
/** Boxes one photo can hold (the cast is at most 3; the rest are "not in the book"). */
const MAX_FACES_PER_PHOTO = 12;

let keyCounter = 0;
/** Keys stay unique across remounts (the builder keeps its photos while the step comes and goes). */
const newKey = (prefix: string) => `${prefix}-${++keyCounter}`;

type Selection = { photo: string; face: string } | null;

interface DragState {
  photo: string;
  face: string;
  pointerId: number;
  startX: number;
  startY: number;
  box: StorybookTaggedFace['box'];
  width: number;
  height: number;
}

/**
 * The photo step (docs/storybook-identity-lock.md §0, §2.2): up to
 * limits.maxPhotos photos, prepared on the device; faces found in the browser
 * (storybook-faces.ts) are drawn as boxes on each photo, and the parent taps a
 * face and picks who it is. A face the detector missed is marked by hand with
 * "Add a face" (a square box, moved by a tap or a drag, sized with a slider).
 * Works by touch at 375px and by keyboard: boxes are buttons, arrow keys move
 * a hand-placed one. Every photo and name here is the family's own, so the
 * whole step is excluded from analytics capture.
 */
export function StorybookPhotoTagger({
  idPrefix,
  people,
  keptPhotos,
  photos,
  onPhotosChange,
  limits,
  onBusyChange,
  error,
  photoErrors,
  onPhotoEdited,
}: StorybookPhotoTaggerProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selection>(null);
  const drag = useRef<DragState | null>(null);
  const mounted = useRef(true);
  /** The photos as last rendered, for work that finishes later (a detection). */
  const photosRef = useRef(photos);
  photosRef.current = photos;
  /** Photos that timed out and were given one more look of their own (never two). */
  const retried = useRef(new Set<string>());
  /** Photos the parent removed (a look still in flight for one is not repeated). */
  const removed = useRef(new Set<string>());
  /** Photos looked at again once another photo's answer came (never twice). */
  const lookedAgain = useRef(new Set<string>());
  const busyReport = useRef(onBusyChange);
  busyReport.current = onBusyChange;
  // The "Who is it?" panel holds the focus and unmounts when it closes: the
  // face box (or "Add a face", when the box was removed) takes it back.
  const boxRefs = useRef(new Map<string, HTMLButtonElement>());
  const addRefs = useRef(new Map<string, HTMLButtonElement>());
  const refocus = useRef<{ box?: string; add?: string; scroll: boolean } | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      busyReport.current?.(false);
    };
  }, []);

  useEffect(() => {
    busyReport.current?.(busy);
  }, [busy]);

  // A selection whose face (or photo) went away is dropped.
  const selectedPhoto = selected ? photos.find((p) => p.key === selected.photo) : undefined;
  const selectedFace = selected ? selectedPhoto?.faces.find((f) => f.key === selected.face) : undefined;
  useEffect(() => {
    if (selected && !selectedFace) setSelected(null);
  }, [selected, selectedFace]);

  // After the render that closed the panel, so a re-enabled "Add a face" can take the focus.
  useEffect(() => {
    const target = refocus.current;
    if (selected || !target) return;
    refocus.current = null;
    const button = target.box ? boxRefs.current.get(target.box) : target.add ? addRefs.current.get(target.add) : undefined;
    button?.focus({ preventScroll: !target.scroll });
  }, [selected, photos]);

  /**
   * Where the focus goes once the panel closes. A keyboard press "clicks"
   * with detail 0 and scrolls the button into view; a tap leaves the page
   * where it is.
   */
  const returnFocus = (target: { box?: string; add?: string }, event?: MouseEvent<HTMLElement>) => {
    refocus.current = { ...target, scroll: !event || event.detail === 0 };
  };

  const closePanel = (face: StorybookTaggedFace, event?: MouseEvent<HTMLElement>) => {
    returnFocus({ box: face.key }, event);
    setSelected(null);
  };

  const taggable = people.filter((person) => person.photoAllowed);
  const coverage = photoCoverage(photos, people);
  const detectorOff = photos.some((photo) => photo.detection === 'unavailable');
  const full = photos.length >= limits.maxPhotos;
  const inputId = `${idPrefix}-input`;
  const helpId = `${idPrefix}-help`;
  const errorId = `${idPrefix}-error`;

  const personName = (key: string | null) => people.find((p) => p.key === key)?.name || '';

  const edited = (photoKey: string) => onPhotoEdited?.(photoKey);

  const findFaces = async (item: Pick<StorybookPhotoItem, 'key' | 'dataUrl'>) => {
    const result = await detectStorybookFaces(item.dataUrl);
    // The first load can outlast the timeout on a slow phone and keeps going
    // (storybook-faces.ts): this photo gets one more look of its own, queued
    // behind that load with a fresh timeout, and keeps "Looking for faces…"
    // meanwhile instead of saying faces can't be found on this device.
    const timedOut = 'reason' in result && result.reason === 'timeout';
    const retry = timedOut && !retried.current.has(item.key) && !removed.current.has(item.key);
    if (retry) retried.current.add(item.key);
    onPhotosChange((prev) =>
      prev.map((photo) => {
        if (photo.key !== item.key) return photo;
        if (!result.ok) return { ...photo, detection: retry ? 'pending' : 'unavailable' };
        const found: StorybookTaggedFace[] = result.faces.map((box) => ({ key: newKey('face'), box, source: 'auto', person: null }));
        // Boxes the parent placed by hand while it was still looking stay.
        return { ...photo, detection: 'done', faces: [...found, ...photo.faces.filter((face) => face.source === 'manual')] };
      }),
    );
    if (retry) {
      void findFaces(item);
      return;
    }
    if (!result.ok) return;
    // The model answers now. A photo that gave up anyway (the load outlasted
    // its own second look too, or its run failed) is looked at once more,
    // unless the parent already marked its faces by hand.
    for (const photo of photosRef.current) {
      if (photo.key === item.key || photo.detection !== 'unavailable' || photo.faces.length || lookedAgain.current.has(photo.key)) continue;
      lookedAgain.current.add(photo.key);
      onPhotosChange((prev) => prev.map((p) => (p.key === photo.key ? { ...p, detection: 'pending' } : p)));
      void findFaces(photo);
    }
  };

  const handleFiles = async (list: FileList | File[] | null | undefined) => {
    const files = Array.from(list || []);
    if (!files.length || busy) return;
    setNotice(null);
    const room = limits.maxPhotos - photos.length;
    if (room <= 0) {
      setNotice(t('storybookPage.photos.errors.maxPhotos', { max: limits.maxPhotos }));
      return;
    }
    const messages = new Set<string>();
    if (files.length > room) messages.add(t('storybookPage.photos.errors.someSkipped', { max: limits.maxPhotos }));
    setBusy(true);
    let used = photoBytes(photos);
    try {
      for (const file of files.slice(0, room)) {
        const left = limits.maxTotalPhotoBytes - used;
        if (left < MIN_PHOTO_BUDGET) {
          messages.add(t('storybookPage.photos.errors.total'));
          break;
        }
        const budget = Math.min(limits.maxPhotoBytes, left);
        try {
          const prepared = await prepareStorybookPhoto(file, { maxBytes: budget, longEdge: limits.photoLongEdge });
          used += prepared.bytes;
          const item: StorybookPhotoItem = { key: newKey('photo'), ...prepared, detection: 'pending', faces: [] };
          onPhotosChange((prev) => [...prev, item]);
          void findFaces(item);
        } catch (err) {
          const code = err instanceof StorybookPhotoError ? err.code : 'unreadable';
          // Too large only because the other photos used up the request's total.
          const key = code === 'too_large' && budget < limits.maxPhotoBytes ? 'total' : ERROR_KEYS[code];
          messages.add(t(`storybookPage.photos.errors.${key}`));
        }
      }
    } finally {
      if (mounted.current) {
        setBusy(false);
        if (messages.size) setNotice([...messages].join(' '));
      }
    }
  };

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (!dragging) setDragging(true);
  };

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    void handleFiles(event.dataTransfer?.files);
  };

  const removePhoto = (photoKey: string) => {
    removed.current.add(photoKey);
    onPhotosChange((prev) => prev.filter((photo) => photo.key !== photoKey));
    edited(photoKey);
    setNotice(null);
  };

  const select = (photoKey: string, faceKey: string) => setSelected({ photo: photoKey, face: faceKey });

  const choose = (photo: StorybookPhotoItem, face: StorybookTaggedFace, person: string, event?: MouseEvent<HTMLElement>) => {
    if (assignBlock(photos, photo.key, face.key, person, people, limits)) return;
    onPhotosChange((prev) => assignFace(prev, photo.key, face.key, person));
    edited(photo.key);
    // One tap per face: the panel closes once someone is picked.
    closePanel(face, event);
  };

  const addFace = (photo: StorybookPhotoItem) => {
    if (photo.faces.length >= MAX_FACES_PER_PHOTO) return;
    const side = Math.max(limits.minFacePx, Math.round(Math.min(photo.width, photo.height) * MANUAL_START_SHARE));
    const face: StorybookTaggedFace = { key: newKey('face'), box: squareFaceBox(0.5, 0.5, side, photo), source: 'manual', person: null };
    onPhotosChange((prev) => prev.map((p) => (p.key === photo.key ? { ...p, faces: [...p.faces, face] } : p)));
    edited(photo.key);
    select(photo.key, face.key);
  };

  const moveFace = (photo: StorybookPhotoItem, face: StorybookTaggedFace, cx: number, cy: number) => {
    const box = squareFaceBox(cx, cy, boxSidePx(face.box, photo), photo);
    onPhotosChange((prev) => updateFaceBox(prev, photo.key, face.key, box));
    edited(photo.key);
  };

  const resizeFace = (photo: StorybookPhotoItem, face: StorybookTaggedFace, side: number) => {
    const [cx, cy] = boxCentre(face.box);
    onPhotosChange((prev) => updateFaceBox(prev, photo.key, face.key, squareFaceBox(cx, cy, side, photo)));
    edited(photo.key);
  };

  const deleteFace = (photo: StorybookPhotoItem, face: StorybookTaggedFace, event?: MouseEvent<HTMLElement>) => {
    onPhotosChange((prev) => removeFace(prev, photo.key, face.key));
    edited(photo.key);
    // Its box is gone: "Add a face" under the same photo takes the focus.
    returnFocus({ add: photo.key }, event);
    setSelected(null);
  };

  // Tap on the photo: the selected hand-placed box jumps there.
  const onPhotoClick = (event: MouseEvent<HTMLDivElement>, photo: StorybookPhotoItem) => {
    if ((event.target as HTMLElement).closest('button')) return;
    const face = selected?.photo === photo.key ? photo.faces.find((f) => f.key === selected.face) : undefined;
    if (!face || face.source !== 'manual') return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!(rect.width > 0) || !(rect.height > 0)) return;
    moveFace(photo, face, (event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height);
  };

  const onBoxPointerDown = (event: PointerEvent<HTMLButtonElement>, photo: StorybookPhotoItem, face: StorybookTaggedFace) => {
    if (face.source !== 'manual' || selected?.face !== face.key) return;
    const frame = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!frame || !(frame.width > 0) || !(frame.height > 0)) return;
    event.preventDefault();
    drag.current = {
      photo: photo.key,
      face: face.key,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      box: face.box,
      width: frame.width,
      height: frame.height,
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* capture is a nicety: the move still follows while over the box */
    }
  };

  const onBoxPointerMove = (event: PointerEvent<HTMLButtonElement>, photo: StorybookPhotoItem, face: StorybookTaggedFace) => {
    const d = drag.current;
    if (!d || d.pointerId !== event.pointerId || d.face !== face.key) return;
    const [cx, cy] = boxCentre(d.box);
    moveFace(photo, face, cx + (event.clientX - d.startX) / d.width, cy + (event.clientY - d.startY) / d.height);
  };

  const endDrag = () => {
    drag.current = null;
  };

  const onBoxKeyDown = (event: KeyboardEvent<HTMLButtonElement>, photo: StorybookPhotoItem, face: StorybookTaggedFace) => {
    if (face.source !== 'manual') return;
    const step = event.shiftKey ? 0.05 : 0.01;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    if (selected?.face !== face.key) select(photo.key, face.key);
    const [cx, cy] = boxCentre(face.box);
    moveFace(photo, face, cx + move[0], cy + move[1]);
  };

  const blockText = (block: StorybookAssignBlock) =>
    block === 'in_photo'
      ? t('storybookPage.photos.who.inPhoto')
      : block === 'max_refs'
        ? t('storybookPage.photos.who.maxRefs', { count: limits.maxRefsPerPerson })
        : '';

  const faceWho = (photo: StorybookPhotoItem, face: StorybookTaggedFace) => {
    if (faceTooSmall(face.box, photo, limits)) return t('storybookPage.photos.who.tooSmallShort');
    if (face.person === NOT_IN_BOOK) return t('storybookPage.photos.who.notInBook');
    return personName(face.person) || t('storybookPage.photos.who.unchosen');
  };

  const statusOf = (photo: StorybookPhotoItem) => {
    if (photo.detection === 'pending') return { text: t('storybookPage.photos.status.detecting'), spin: true };
    if (!photo.faces.length) {
      return { text: t(photo.detection === 'unavailable' ? 'storybookPage.photos.status.manual' : 'storybookPage.photos.status.noFaces') };
    }
    if (photo.faces.some((face) => face.person === null && !faceTooSmall(face.box, photo, limits))) {
      return { text: t('storybookPage.photos.status.tapFaces') };
    }
    if (photoIsUsed(photo, people, limits)) {
      const names = [...new Set(photo.faces.map((face) => face.person).filter((p) => p && p !== NOT_IN_BOOK))]
        .map((key) => personName(key))
        .filter(Boolean);
      return { text: t('storybookPage.photos.status.ready', { names: names.join(', ') }), ok: true };
    }
    return { text: t('storybookPage.photos.status.notUsed'), warn: true };
  };

  return (
    <div className="ph-no-capture space-y-6">
      {full ? (
        <p className="rounded-2xl border border-dashed border-input bg-muted/40 px-4 py-3 text-center text-sm text-muted-foreground">
          {t('storybookPage.photos.full', { max: limits.maxPhotos })}
        </p>
      ) : (
        <label
          data-consent-tool="storybook"
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed bg-background px-4 py-7 text-center transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 hover:border-primary/60 hover:bg-primary/5',
            dragging ? 'border-primary bg-primary/10' : error ? 'border-destructive/70' : 'border-input',
            busy && 'pointer-events-none opacity-70',
          )}
        >
          <input
            id={inputId}
            type="file" data-consent-tool="storybook"
            multiple
            accept={STORYBOOK_PHOTO_ACCEPT}
            className="sr-only"
            aria-describedby={[helpId, error ? errorId : null].filter(Boolean).join(' ')}
            aria-invalid={error ? true : undefined}
            disabled={busy}
            onChange={(event) => {
              const files = event.target.files ? Array.from(event.target.files) : [];
              // Cleared so choosing the same file again still fires onChange.
              event.target.value = '';
              void handleFiles(files);
            }}
          />
          {busy ? (
            <Loader2 className="h-7 w-7 animate-spin text-primary" aria-hidden="true" />
          ) : (
            <ImagePlus className="h-7 w-7 text-primary" aria-hidden="true" />
          )}
          <span className="text-sm font-semibold">
            {busy ? t('storybookPage.photos.processing') : t('storybookPage.photos.drop')}
          </span>
          <span className="text-xs text-muted-foreground">
            {photos.length
              ? t('storybookPage.photos.count', { count: photos.length, max: limits.maxPhotos })
              : t('storybookPage.photos.formats', { max: limits.maxPhotos })}
          </span>
        </label>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-secondary/50 p-4 text-sm text-secondary-foreground">
          <p className="flex items-center gap-2 font-semibold">
            <Camera className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            {t('storybookPage.photos.tips.title')}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-secondary-foreground/90">
            <li>{t('storybookPage.photos.tips.recent')}</li>
            <li>{t('storybookPage.photos.tips.clear')}</li>
            <li>{t('storybookPage.photos.tips.big')}</li>
            <li>{t('storybookPage.photos.tips.family')}</li>
          </ul>
        </div>

        <section aria-labelledby={`${idPrefix}-coverage-title`} className="rounded-2xl border border-border bg-card p-4 text-sm">
          <h4 id={`${idPrefix}-coverage-title`} className="font-semibold">
            {t('storybookPage.photos.coverage.title')}
          </h4>
          <ul className="mt-2 space-y-1.5" data-testid={`${idPrefix}-coverage`}>
            {people.map((person) => {
              const count = coverage[person.key] || 0;
              const kept = person.photoAllowed && count === 0 && Boolean(keptPhotos?.has(person.key));
              return (
                <li key={person.key} className="flex items-start gap-2">
                  {count > 0 || kept || person.avatar ? (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
                  ) : (
                    <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <span className="min-w-0 break-words">
                    {person.avatar
                      ? t('storybookPage.photos.coverage.avatar', { name: person.name, avatar: person.avatar })
                      : !person.photoAllowed
                      ? t('storybookPage.photos.coverage.noPhotos', { name: person.name })
                      : count > 0
                        ? t('storybookPage.photos.coverage.photos', { name: person.name, count })
                        : kept
                          ? t('storybookPage.photos.coverage.kept', { name: person.name })
                          : t('storybookPage.photos.coverage.description', { name: person.name })}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <p id={helpId} className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
        {t('storybookPage.photos.privacy')}
      </p>

      {notice && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {notice}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
      {detectorOff && (
        <p role="status" className="flex items-start gap-2 rounded-2xl border border-border bg-muted/50 p-3 text-sm text-foreground">
          <ScanFace className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {t('storybookPage.photos.manualNotice')}
        </p>
      )}

      {photos.length > 0 && (
        <ol className="space-y-5">
          {photos.map((photo, photoIndex) => {
            const n = photoIndex + 1;
            const status = statusOf(photo);
            const face = selected?.photo === photo.key ? photo.faces.find((f) => f.key === selected.face) : undefined;
            const faceIndex = face ? photo.faces.indexOf(face) + 1 : 0;
            const tooSmall = face ? faceTooSmall(face.box, photo, limits) : false;
            const panelId = `${idPrefix}-who-${photo.key}`;
            const minSide = Math.max(16, Math.round(limits.minFacePx / 2));
            const maxSide = Math.min(photo.width, photo.height);
            const ratio = photo.width / photo.height;
            return (
              <li key={photo.key} className="rounded-2xl border border-border bg-card p-3 sm:p-4" data-testid="storybook-photo">
                <div
                  className="relative mx-auto overflow-hidden rounded-xl bg-muted"
                  // Exactly the photo's shape, and never taller than most of the screen.
                  style={{ aspectRatio: `${photo.width} / ${photo.height}`, width: `min(100%, ${(62 * ratio).toFixed(2)}vh)` }}
                  onClick={(event) => onPhotoClick(event, photo)}
                >
                  <img
                    src={photo.dataUrl}
                    alt={t('storybookPage.photos.photoAlt', { n })}
                    draggable={false}
                    className="ph-no-capture absolute inset-0 h-full w-full select-none object-fill"
                  />
                  {photo.faces.map((f, i) => {
                    const small = faceTooSmall(f.box, photo, limits);
                    const isSelected = selected?.photo === photo.key && selected.face === f.key;
                    const assigned = f.person && f.person !== NOT_IN_BOOK;
                    const label = faceWho(photo, f);
                    const labelBelow = f.box[3] < 0.86;
                    return (
                      <button
                        key={f.key}
                        ref={(el) => {
                          if (el) boxRefs.current.set(f.key, el);
                          else boxRefs.current.delete(f.key);
                        }}
                        type="button"
                        aria-label={t('storybookPage.photos.faceLabel', { n: i + 1, photo: n, who: label })}
                        aria-pressed={isSelected}
                        aria-controls={isSelected ? panelId : undefined}
                        onClick={() => select(photo.key, f.key)}
                        onKeyDown={(event) => onBoxKeyDown(event, photo, f)}
                        onPointerDown={(event) => onBoxPointerDown(event, photo, f)}
                        onPointerMove={(event) => onBoxPointerMove(event, photo, f)}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        className={cn(
                          "absolute rounded-lg border-2 transition-shadow before:absolute before:-inset-3 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
                          small
                            ? 'border-dashed border-destructive bg-destructive/10'
                            : assigned
                              ? 'border-primary bg-primary/10'
                              : f.person === NOT_IN_BOOK
                                ? 'border-white/70 bg-black/30'
                                : 'border-dashed border-white bg-white/10 shadow-[0_0_0_1px_rgba(0,0,0,0.45)]',
                          isSelected && 'shadow-[0_0_0_3px_hsl(var(--primary)),0_0_0_5px_white]',
                          f.source === 'manual' && isSelected && 'cursor-move touch-none',
                        )}
                        style={{
                          left: `${f.box[0] * 100}%`,
                          top: `${f.box[1] * 100}%`,
                          width: `${(f.box[2] - f.box[0]) * 100}%`,
                          height: `${(f.box[3] - f.box[1]) * 100}%`,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            'pointer-events-none absolute left-1/2 max-w-[9rem] -translate-x-1/2 truncate whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold leading-tight shadow',
                            labelBelow ? 'top-full mt-1' : 'bottom-full mb-1',
                            small
                              ? 'bg-destructive text-destructive-foreground'
                              : assigned
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-white text-foreground',
                          )}
                        >
                          {assigned || small || f.person === NOT_IN_BOOK ? label : `${i + 1} ?`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <p
                  className={cn(
                    'mt-3 flex items-start gap-2 text-sm',
                    status.ok ? 'font-medium text-emerald-800' : status.warn ? 'text-muted-foreground' : 'text-foreground',
                  )}
                  role="status"
                >
                  {status.spin && <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />}
                  {status.ok && <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
                  <span className="min-w-0">{status.text}</span>
                </p>
                {photoErrors?.[photo.key] && (
                  <p role="alert" className="mt-2 text-sm font-medium text-destructive">
                    {photoErrors[photo.key]}
                  </p>
                )}

                {face && (
                  <div
                    id={panelId}
                    role="group"
                    aria-labelledby={`${panelId}-title`}
                    className="mt-3 space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-3"
                  >
                    <p id={`${panelId}-title`} className="text-sm font-semibold">
                      {t('storybookPage.photos.who.title', { n: faceIndex })}
                    </p>
                    {tooSmall ? (
                      <p className="text-sm font-medium text-destructive">{t('storybookPage.photos.who.tooSmall')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {taggable.map((person) => {
                          const block = assignBlock(photos, photo.key, face.key, person.key, people, limits);
                          const pressed = face.person === person.key;
                          return (
                            <button
                              key={person.key}
                              type="button"
                              aria-pressed={pressed}
                              disabled={Boolean(block)}
                              onClick={(event) => choose(photo, face, person.key, event)}
                              className={cn(
                                'inline-flex min-h-[2.75rem] max-w-full flex-col items-start justify-center rounded-2xl border px-3.5 py-1.5 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                pressed ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:border-primary/60',
                                block && 'cursor-not-allowed opacity-60',
                              )}
                            >
                              <span className="max-w-full truncate">{person.name}</span>
                              {block && (
                                <span className="text-[11px] font-normal">
                                  <span className="sr-only">, </span>
                                  {blockText(block)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          aria-pressed={face.person === NOT_IN_BOOK}
                          onClick={(event) => choose(photo, face, NOT_IN_BOOK, event)}
                          className={cn(
                            'inline-flex min-h-[2.75rem] items-center rounded-2xl border px-3.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            face.person === NOT_IN_BOOK ? 'border-foreground bg-foreground text-background' : 'border-border bg-card hover:border-primary/60',
                          )}
                        >
                          {t('storybookPage.photos.who.notInBook')}
                        </button>
                      </div>
                    )}

                    {face.source === 'manual' && (
                      <div className="space-y-2 border-t border-primary/20 pt-3">
                        <p className="text-xs text-muted-foreground">{t('storybookPage.photos.manual.hint')}</p>
                        <label className="flex items-center gap-3 text-sm font-medium" htmlFor={`${panelId}-size`}>
                          <span className="shrink-0">{t('storybookPage.photos.manual.size')}</span>
                          <input
                            id={`${panelId}-size`}
                            type="range"
                            min={minSide}
                            max={maxSide}
                            step={1}
                            value={Math.min(maxSide, Math.max(minSide, boxSidePx(face.box, photo)))}
                            onChange={(event) => resizeFace(photo, face, Number(event.target.value))}
                            className="h-8 w-full min-w-0 accent-[hsl(var(--primary))]"
                          />
                        </label>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {face.source === 'manual' && (
                        <Button type="button" variant="outline" size="sm" onClick={(event) => deleteFace(photo, face, event)}>
                          <Trash2 aria-hidden="true" />
                          {t('storybookPage.photos.manual.remove')}
                        </Button>
                      )}
                      <Button type="button" variant="ghost" size="sm" onClick={(event) => closePanel(face, event)}>
                        {t('storybookPage.photos.who.done')}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    ref={(el) => {
                      if (el) addRefs.current.set(photo.key, el);
                      else addRefs.current.delete(photo.key);
                    }}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addFace(photo)}
                    disabled={photo.faces.length >= MAX_FACES_PER_PHOTO}
                    aria-label={t('storybookPage.photos.manual.addAria', { n })}
                  >
                    <Plus aria-hidden="true" />
                    {t('storybookPage.photos.manual.add')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => removePhoto(photo.key)}
                    aria-label={t('storybookPage.photos.removeAria', { n })}
                  >
                    <Trash2 aria-hidden="true" />
                    {t('storybookPage.photos.remove')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
